// src/utils/exporters/pdfVehiculosCore.js
/**
 * @file pdfVehiculosCore.js
 * @description Helpers compartidos para generación de PDFs de vehículos
 */

import { VEHICULOS_SCHEMA } from '../../data/vehiculosColumns.schema';
import { getExportTimestamp } from '../formatters/dates';

// -------------------- Formateo básico --------------------
export function asDashIfEmpty(v) {
  return v === null || v === undefined || v === '' ? '-' : v;
}

export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'string') {
    const ddmm = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(v);
    if (ddmm) {
      const d = new Date(parseInt(ddmm[3], 10), parseInt(ddmm[2], 10) - 1, parseInt(ddmm[1], 10));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(v) {
  const d = toDate(v);
  if (!d) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function formatDateTime(v) {
  const d = toDate(v);
  if (!d) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

// -------------------- Resumen por marca / estatus --------------------
export function buildStatusName(estatus) {
  const map = {
    1: 'Inventario',
    3: 'Unidades por arribar',
    6: 'Facturado no entregado',
  };
  return map[estatus] || 'Otro';
}

export function buildSummary(rows) {
  const total = rows.length;
  const marcas = {};

  rows.forEach((row) => {
    const marca = row.Marca || 'Sin marca';
    const statusName = buildStatusName(row.estatus);

    if (!marcas[marca]) {
      marcas[marca] = { total: 0, byStatus: {} };
    }
    marcas[marca].total += 1;
    marcas[marca].byStatus[statusName] =
      (marcas[marca].byStatus[statusName] || 0) + 1;
  });

  return { total, marcas };
}

// -------------------- Lógica de Layout Dinámico --------------------
export function getDynamicLayoutSettings(visibleColumns) {
  const colCount = visibleColumns.length;
  let finalFontSize = 8; // Default para search (un poco más grande que el popup status exporter)
  let pageSize = 'A4';

  if (colCount > 10) {
    finalFontSize = 7;
  }
  if (colCount > 12) {
    pageSize = 'LEGAL';
    finalFontSize = 6;
  }
  if (colCount > 14) {
    finalFontSize = 5;
  }

  // Pesos relativos optimizados para alta densidad de columnas
  const getSafeColumnWidth = (key) => {
    switch (key) {
      case 'detectado_rfid':
      case 'EnSitio': return '2%';
      case 'AnoModelo': return '3%';
      case 'Catalogo': return '3%';
      case 'Marca': return '4%';
      case 'ColorExterior':
      case 'ColorInterior': return '5%';
      case 'FechaRecibo':
      case 'last_read':
      case 'estatus':
      case 'BID': return '6%';
      case 'VIN':
      case 'VTE_DOCTO': return '8%'; // VIN y Factura son códigos largos pero de ancho fijo
      default: return '*'; // El resto (Familia, Vendedor, Ubicacion, Estado) reciben el espacio sobrante (~36% para 4 cols = ~9% c/u)
    }
  };

  const widths = visibleColumns.map(getSafeColumnWidth);

  return { pageSize, finalFontSize, widths };
}

// -------------------- Normalización de tabla --------------------
export function buildTableData(rows, visibleColumns) {
  const headerRow = visibleColumns.map((key) => {
    const col = VEHICULOS_SCHEMA[key] || {};
    return col.header || key;
  });

  const bodyRows = rows.map((row) =>
    visibleColumns.map((key) => {
      const col = VEHICULOS_SCHEMA[key] || {};
      const raw = typeof col.value === 'function' ? col.value(row) : row[key];

      if (col.type === 'boolean') {
        if (raw === true) return 'Sí';
        if (raw === false) return 'No';
        return '-';
      }
      if (col.type === 'date') return formatDate(raw);
      if (col.type === 'datetime') return formatDateTime(raw);
      return asDashIfEmpty(raw);
    })
  );

  return { headerRow, bodyRows };
}

/**
 * Construye una tabla "bonita" de vehículos con header oscuro y zebra.
 */
export function buildVehiculosTable(headerRow, bodyRows, extraMarginTop = 16, options = {}) {
  const {
    widths = Array(headerRow.length).fill('auto'),
    fontSize = 8
  } = options;

  return {
    table: {
      headerRows: 1,
      widths: widths,
      body: [
        headerRow.map((h) => ({
          text: h,
          style: 'tableHeader',
          fillColor: '#1F2937',
          color: '#FFFFFF',
          bold: true,
          fontSize: fontSize + 1, // Header un poco más grande
          alignment: 'center',
          margin: [4, 6, 4, 6],
        })),
        ...bodyRows.map((row, rowIndex) =>
          row.map((cell) => ({
            text: cell,
            style: 'tableCell',
            fillColor: rowIndex % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
            fontSize: fontSize,
            margin: [4, 4, 4, 4],
          }))
        ),
      ],
    },
    layout: {
      hLineWidth: (i) => (i === 0 || i === 1 ? 2 : 0.5),
      vLineWidth: () => 0.5,
      hLineColor: (i) => (i === 0 || i === 1 ? '#1F2937' : '#E5E7EB'),
      vLineColor: () => '#E5E7EB',
      paddingLeft: () => 4,
      paddingRight: () => 4,
      paddingTop: () => 4,
      paddingBottom: () => 4,
    },
    margin: [0, extraMarginTop, 0, 0],
  };
}

// -------------------- Bloques de resumen reutilizables --------------------
/**
 * Bloque: barra azul con total.
 */
export function buildTotalBar(text, fontSize = 12) {
  return {
    table: {
      widths: ['*'],
      body: [[{
        text,
        style: 'summaryTotal',
        fillColor: '#3B82F6',
        color: '#FFFFFF',
        margin: [8, 6, 8, 6],
        bold: true,
        fontSize,
      }]],
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 12],
  };
}

/**
 * Bloques tipo "cards" por marca (resumen por marca).
 */
export function buildMarcaCards(summary) {
  const blocks = [];

  Object.entries(summary.marcas).forEach(([marca, datosMarca]) => {
    blocks.push({
      table: {
        widths: ['*'],
        body: [[{
          stack: [
            {
              text: `${marca} (${datosMarca.total})`,
              style: 'summaryBrand',
              margin: [0, 0, 0, 4],
            },
            ...Object.entries(datosMarca.byStatus).map(
              ([statusName, count]) => ({
                text: `• ${statusName}: ${count}`,
                margin: [8, 2, 0, 2],
                fontSize: 9,
                color: '#4B5563',
              })
            ),
          ],
          fillColor: '#F9FAFB',
          margin: [10, 8, 10, 8],
        }]],
      },
      layout: {
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#E5E7EB',
        vLineColor: () => '#E5E7EB',
      },
      margin: [0, 0, 0, 6],
    });
  });

  return blocks;
}

// -------------------- Filename & estilos --------------------
export function buildFilename(meta = {}) {
  const ts = getExportTimestamp();

  const parts = ['vehiculos'];
  if (meta.sitio) parts.push(meta.sitio.replace(/\s+/g, '_'));
  if (meta.subSitio) parts.push(meta.subSitio.replace(/\s+/g, '_'));
  parts.push(ts);
  return parts.join('_');
}

export const pdfStylesBase = {
  title: {
    fontSize: 18,
    bold: true,
  },
  subheader: {
    fontSize: 12,
    bold: true,
    color: '#1F2937',
  },
  summaryBrand: {
    fontSize: 10,
    bold: true,
    color: '#1F2937',
  },
  summaryTotal: {
    fontSize: 11,
    bold: true,
  },
  tableHeader: {
    bold: true,
    fontSize: 9,
  },
  tableCell: {
    fontSize: 8,
  },
  subsitioTitle: {
    fontSize: 14,
    bold: true,
    color: '#111827',
  },
};

export const pdfDefaultStyle = {
  font: 'Roboto',
  fontSize: 9,
};